import { Link } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import type { Project } from '@mockd/shared';
import { LimitIndicator } from '../common';
import { getMockApiSubdomainUrl } from '../../config';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

interface ProjectRowProps {
  project: Project;
  endpointCount?: number;
  endpointLimit?: number;
}

// An undefined endpointCount means the count couldn't be fetched: omit it rather than claim 0
export function ProjectRow({ project, endpointCount, endpointLimit }: ProjectRowProps) {
  const isAnonymous = !project.userId;

  const subdomainUrl = getMockApiSubdomainUrl(project.subdomain);
  // Strip protocol for display (e.g. "dev.mockd.sh")
  const subdomainDisplay = subdomainUrl.replace(/^https?:\/\//, '');

  const updatedAgo = formatRelativeTime(project.updatedAt);

  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center gap-4 px-4 py-3 bg-base-100 hover:brightness-125 transition-all border-b border-base-300 last:border-b-0"
    >
      {/* Name + subdomain */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-base-content truncate">
            {isAnonymous ? 'Instant Mock' : project.name}
          </span>
          {isAnonymous && (
            <span className="badge badge-ghost badge-xs">temp</span>
          )}
        </div>
        {/* Mobile: the desktop-only columns, inline below the name */}
        <div className="flex items-center gap-2 mt-0.5 md:hidden">
          <code className="text-xs bg-neutral text-neutral-content px-1.5 py-0.5 rounded-field truncate">
            {subdomainDisplay}
          </code>
          {endpointCount != null && (
            <span className="text-xs text-base-content/60 whitespace-nowrap">
              {endpointLimit != null ? `${endpointCount}/${endpointLimit}` : endpointCount}{' '}
              {endpointCount === 1 && endpointLimit == null ? 'endpoint' : 'endpoints'}
            </span>
          )}
          <span className="text-xs text-base-content/70 whitespace-nowrap">Updated {updatedAgo}</span>
        </div>
      </div>

      {/* Subdomain — desktop only */}
      <div className="hidden md:block w-48 shrink-0">
        <code className="text-xs bg-neutral text-neutral-content px-1.5 py-0.5 rounded-field">
          {subdomainDisplay}
        </code>
      </div>

      {/* Endpoints — desktop only */}
      <div className="hidden md:block w-36 shrink-0">
        {endpointCount == null ? null : endpointLimit != null ? (
          <LimitIndicator used={endpointCount} max={endpointLimit} label="Endpoints" />
        ) : (
          <span className="text-sm text-base-content/70">
            {endpointCount} {endpointCount === 1 ? 'endpoint' : 'endpoints'}
          </span>
        )}
      </div>

      {/* Updated — desktop only */}
      <div className="hidden md:block w-28 shrink-0 text-right">
        <span className="text-xs text-base-content/70">Updated {updatedAgo}</span>
      </div>

      {/* Chevron */}
      <ChevronRightIcon className="size-4 text-base-content/60 shrink-0" />
    </Link>
  );
}
